// =============================================================================
// statusVocab — 統一ステータス語彙 (2026-07-08 要確認意味分裂の包括修正)
//
//   表示ラベルの追加・変更はこのファイル経由のみ (council裁定4)。
//   語彙体系の正本は docs/status_vocab_unification.md §3.2 (6語+サブラベル体系)。
//   council 6_ux_architect.md の StatusBadge 9種案は本体系と競合するため破棄済み。
//   色の正本はこのファイル (InvoiceRunsPage パレットCと同値をローカル定義 — 原則C対応)。
//
//   統一語彙は4値固定 (2レイヤ・モデルの「行動レイヤ」):
//     processing      = システム作業中。ユーザーは待つだけ (青)
//     action_required = ユーザーの操作で初めて前進する (橙)
//                       sub: approve=承認判断 / fix=修正・再実行 / judge=裁定(重複判定・監査確認・ルール判定)
//     done            = この画面での処理は終端に到達 (緑)
//     excluded        = 対象外 (ユーザー除外・却下) (グレー)
//
//   赤は「失敗・不一致という事実」(事実レイヤ: 理由チップ・severity=high・HealthBanner) 専用。
//   行のワークフローバッジには使わない。
//
//   禁止語 (UI文言として新規使用禁止・grep 0件を維持):
//     「要確認」「要対応」「確認待ち」「確認保留」「判定保留」
//
//   件数をサブ種別付きで出す時は必ず着地画面名を併記する
//   (例:「対応待ち 49件 — 承認へ 2件(#invoice)・エラー対応 47件(この表)」)。
//   「49件と言われたのに行き先に2件しかない」誤解の構造的な再発防止。
// =============================================================================

export type UnifiedStatus = 'processing' | 'action_required' | 'done' | 'excluded';
export type ActionSub = 'approve' | 'fix' | 'judge' | null;
export interface UnifiedBadge { status: UnifiedStatus; sub: ActionSub; label: string; bg: string; fg: string; bd: string; }

const COLOR: Record<UnifiedStatus, { bg: string; fg: string; bd: string }> = {
  processing:      { bg: '#f0f6ff', fg: '#33699e', bd: '#b9d2ed' },
  action_required: { bg: '#fdf3e3', fg: '#a46a12', bd: '#e3c68a' },
  done:            { bg: '#edf8f4', fg: '#267a62', bd: '#9fdbc7' },
  excluded:        { bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df' },
};

// 4色の正本を外部公開 (ページ側でバッジ色をハードコードしないための参照点)
export const UNIFIED_COLOR = COLOR;

const mk = (status: UnifiedStatus, sub: ActionSub, label: string): UnifiedBadge =>
  ({ status, sub, label, ...COLOR[status] });

// ヘッダ/バッジ近くに添える1行の行動説明 (「この語が出たら何をすべきか」の正本)
export const ACTION_HINT: Record<'approve' | 'fix' | 'judge', string> = {
  approve: '内容を確認して承認/却下してください',
  fix: '理由を確認して修正・再実行してください (処理はそこで止まっています)',
  judge: '修正ではなく、あなたの判定で確定してください',
};
export const unifiedHint = (b: UnifiedBadge): string => {
  if (b.status === 'processing') return 'システム作業中 — あなたの操作は不要です';
  if (b.status === 'action_required') return ACTION_HINT[b.sub || 'fix'];
  if (b.status === 'done') return 'この画面での処理は完了しています';
  return '対象外です (操作不要)';
};

// 全ページ共通の色凡例 (ヘッダ併記用の1行)
export const UNIFIED_LEGEND =
  '橙=対応待ち(あなたの番) ／ 青=処理中(操作不要) ／ 緑=完了 ／ グレー=除外。赤は失敗・不一致の事実表示のみ';

// invoice_run_items.state (+note) → 統一語彙。note を state より優先する判定あり。
export function runItemToUnified(state: string, note?: string | null): UnifiedBadge {
  const n = String(note || '').replace(/\|auto_rebuild_tried$/, '');
  if (/^re_extract_(pending|dispatched)/.test(n)) return mk('processing', null, '処理中(再読取中)');
  switch (state) {
    case 'dispatched': case 'queued': case 'waiting':
      return mk('processing', null, '処理中(順番待ち)');
    case 'extracting': case 'claimed': case 'running':
      return mk('processing', null, '処理中(読取中)');
    case 'extracted':
      return mk('processing', null, '処理中(仕訳作成中)');
    case 'pending':
      return mk('action_required', 'fix', '対応待ち(開始失敗)');
    case 'failed': case 'error': case 'dead_letter':
      return mk('action_required', 'fix', '対応待ち(処理失敗)');
    case 'needs_review': case 'blocked': case 'review':
      return mk('action_required', 'fix', '対応待ち(要修正)');
    case 'proposed': case 'proposal_done': case 'succeeded':
      return mk('done', null, '完了(→承認へ)');
    case 'excluded': case 'skipped':
      return mk('excluded', null, '除外');
    default:
      return mk('action_required', 'fix', '対応待ち(不明)'); // 生state は呼び元が <details> に退避
  }
}

// 仕訳検証ページ: entries[].status → 統一語彙。
//   deferred由来 (entryId が 'deferred_' 前置) はエラー対応、proposal由来は承認待ち。
export function verificationStatusToUnified(
  vs: string, entryId?: string, hasSentInstruction?: boolean,
): UnifiedBadge {
  if (hasSentInstruction) return mk('processing', null, '処理中(指示対応中)');
  switch (vs) {
    case 'approved': return mk('done', null, '完了(承認済/登録済)');
    case 'blocked':  return mk('excluded', null, '除外(却下)');
    case 'pending':  return mk('processing', null, '処理中');
    case 'needs_review':
      return String(entryId || '').startsWith('deferred_')
        ? mk('action_required', 'fix', '対応待ち(エラー対応)')
        : mk('action_required', 'approve', '対応待ち(承認)');
    default: return mk('processing', null, '処理中');
  }
}

// =============================================================================
// 展開マッパー群 (docs/status_vocab_unification.md §3.5)
//   すべて mk() 経由・COLOR 4色のみ使用。生の内部トークンは呼び元が tooltip/<details> に退避する。
// =============================================================================

// invoice_runs.status (run そのもの) → 統一語彙。
//   attention = run内に人の対応が必要なitemが残存 (runの失敗ではない) → 橙「対応待ちあり」。
//   件数内訳 (承認へ n件・要修正 n件) は呼び元が必ず併記する (§3.1-5)。
export function runStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'open': return mk('processing', null, '処理中');
    case 'attention': return mk('action_required', 'fix', '対応待ちあり');
    case 'closed': return mk('excluded', null, '完了(close済)');
    default: return mk('processing', null, s || '処理中');
  }
}

// batches.status → 統一語彙。attention = 中に失敗/不一致/停滞あり → 修正・再試行が必要。
export function batchStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'open': return mk('processing', null, '処理中(照合待ち)');
    case 'attention': return mk('action_required', 'fix', '対応待ち(要修正)');
    case 'closed': return mk('done', null, '完了(close済)');
    default: return mk('processing', null, s || '処理中');
  }
}

// batch_items.state → 統一語彙。posted は機械照合を待つだけ (ユーザー操作不要=青)。
//   verify_failed/failed の「照合不一致/失敗」は赤の事実チップとして呼び元が別掲する。
export function batchItemToUnified(state: string): UnifiedBadge {
  switch (state) {
    case 'pending': return mk('processing', null, '処理中(順番待ち)');
    case 'registering': return mk('processing', null, '処理中(登録中)');
    case 'posted': return mk('processing', null, '処理中(照合待ち)');
    case 'verified': return mk('done', null, '完了(照合済)');
    case 'verify_failed': case 'failed': return mk('action_required', 'fix', '対応待ち(要修正)');
    default: return mk('processing', null, state || '処理中');
  }
}

// ジョブqueueの status → 統一語彙。failed/dead_letter は再試行で前進する=あなたの番(橙)。
export function jobStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'awaiting_approval': return mk('action_required', 'approve', '対応待ち(承認)');
    case 'failed': case 'dead_letter': return mk('action_required', 'fix', '対応待ち(処理失敗)');
    case 'queued': return mk('processing', null, '処理中(順番待ち)');
    case 'claimed': case 'running': return mk('processing', null, '処理中(実行中)');
    case 'succeeded': return mk('done', null, '完了(成功)');
    case 'cancelled': return mk('excluded', null, '除外(取消)');
    default: return mk('processing', null, s || '処理中');
  }
}

// findings.status → 統一語彙。open は修正ではなく「確認済にする or 理由付き却下」の裁定 → sub=judge。
export function findingStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'open': return mk('action_required', 'judge', '対応待ち(監査確認)');
    case 'confirmed': return mk('done', null, '完了(確認済)');
    case 'dismissed': return mk('excluded', null, '除外(却下)');
    default: return mk('processing', null, s || '処理中');
  }
}

// 日報dealのstatus → 統一語彙。blocked_no_bank は銀行入金の自動到着・再評価で解消する
//   (ユーザー操作不要) → 橙ではなく青「処理中(入金待ち)」。停止理由 (貸借不一致等) は事実レイヤで別掲。
export function nippouStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'suggested': return mk('action_required', 'approve', '対応待ち(承認)');
    case 'proposed': return mk('action_required', 'approve', '対応待ち(登録可)');
    case 'approved': return mk('processing', null, '承認済(登録待ち)');
    case 'registered': return mk('done', null, '完了(登録済)');
    case 'rejected': return mk('excluded', null, '除外(却下)');
    case 'error': return mk('action_required', 'fix', '対応待ち(処理失敗)');
    case 'held_kakeuri': return mk('action_required', 'fix', '対応待ち(指示: 掛売)');
    case 'held_unknown': return mk('action_required', 'fix', '対応待ち(要修正: 未知分類)');
    case 'blocked_no_bank': return mk('processing', null, '処理中(入金待ち)');
    case 'held_imbalance': return mk('action_required', 'fix', '対応待ち(要修正: 貸借不一致)');
    case 'held_bank_unmatched': return mk('action_required', 'fix', '対応待ち(要修正: 銀行照合不可)');
    default: return mk('action_required', 'fix', s || '対応待ち(不明)');
  }
}

// InvoiceCoverageTab の UiStatus → 統一語彙。unprocessed=取込を開始すれば前進する=あなたの番(橙・赤をやめる)。
export function coverageUiStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'unprocessed': return mk('action_required', 'fix', '対応待ち(取込)');
    case 'in_progress': return mk('processing', null, '処理中');
    case 'needs_fix': return mk('action_required', 'fix', '対応待ち(要修正)');
    case 'done': return mk('done', null, '完了(処理済)');
    case 'rejected': return mk('excluded', null, '除外(却下)');
    default: return mk('processing', null, s || '処理中');
  }
}

// 銀行明細パイプライン processing_status → 統一語彙。
//   suggested/needs_review = AI提案済みで人の確認待ち = 承認系 (エラーではない)。
export function bankPipelineToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'suggested': case 'needs_review': return mk('action_required', 'approve', '対応待ち(承認)');
    case 'error': return mk('action_required', 'fix', '対応待ち(処理失敗)');
    case 'queued': case 'processing': return mk('processing', null, '処理中(分析中)');
    case 'approved': return mk('done', null, '完了(承認済)');
    case 'registered': return mk('done', null, '完了(登録済)');
    case 'skipped': return mk('excluded', null, '除外(スキップ)');
    default: return mk('processing', null, s || '処理中');
  }
}

// 銀行明細の消込副状態 reconcile_state → 統一語彙 (2026-07-13, worker list API 追加予定フィールド)。
//   processing_status='error' の行のうち、実は「エラー対応不要」なサブ状態を区別するための語彙。
//   reconcile_pending = 消込が sweep で自動回復する見込み → ユーザーは待つだけ (青)。
//   freee_fee_drift   = freee 側と手数料差額あり → 人の裁定で確定 → sub=judge (橙)。
//   未知の値 / null / undefined は null を返し、呼び元は通常のエラー表示に fallback する
//   (= 応答に reconcile_state が無くても UI は壊れない)。
export function bankReconcileStateToUnified(rs: string | null | undefined): UnifiedBadge | null {
  switch (rs) {
    case 'reconcile_pending': return mk('processing', null, '消込待ち(自動回復中)');
    case 'freee_fee_drift': return mk('action_required', 'judge', '差額要確認');
    default: return null;
  }
}

// dedup判定結果 → 統一語彙。duplicate_review は人の裁定 (重複orユニーク) が必要 → sub=judge。
//   dedup_reason は事実レイヤとして呼び元が別掲する。
export function duplicateStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'duplicate_review': return mk('action_required', 'judge', '対応待ち(重複判定)');
    case 'duplicate_rejected': return mk('excluded', null, '除外(自動判定)');
    default: return mk('processing', null, s || '処理中');
  }
}

// 口座引落proposalのstatus → 統一語彙。
//   pending = 承認判断待ち(通常フロー) / needs_review = 不備あり(修正が必要) — ここが旧「要確認」の意味衝突点。
export function autoDebitProposalToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'pending': return mk('action_required', 'approve', '対応待ち(承認)');
    case 'needs_review': return mk('action_required', 'fix', '対応待ち(要修正)');
    case 'approved': return mk('done', null, '完了(承認済)');
    case 'blocked': return mk('excluded', null, '除外(却下)');
    default: return mk('action_required', 'approve', '対応待ち(承認)');
  }
}

// 定額仕訳の月次状態 → 統一語彙。pending = 今月分の登録実行(承認)を待つ。
export function fixedJournalStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'pending': return mk('action_required', 'approve', '対応待ち(今月登録)');
    case 'registered': return mk('done', null, '完了(登録済)');
    case 'skipped': return mk('excluded', null, '除外(今月スキップ)');
    case 'disabled': return mk('excluded', null, '除外(対象外)');
    default: return mk('action_required', 'approve', '対応待ち(今月登録)');
  }
}

// 仕訳への修正指示 corrections.status → 統一語彙。
//   open = 指示送信済で担当(Claude)の作業待ち = ユーザーは待つだけ → 青「処理中(指示対応中)」。
export function correctionStatusToUnified(s: string): UnifiedBadge {
  switch (s) {
    case 'open': case 'in_progress': return mk('processing', null, '処理中(指示対応中)');
    case 'done': return mk('done', null, '完了(解決済)');
    default: return mk('excluded', null, '除外(取下げ)');
  }
}
